import React from 'react';
import styled from 'styled-components';
import { hexToRgba } from '@mrkt/features/color-conversion';
import {
  screenMdMin,
  screenSmMax,
  spacer16,
} from '@spotify-internal/encore-foundation';
import { IconArrowRight, Type } from '@spotify-internal/encore-web';
import {
  Link,
  ButtonTertiaryAsLink,
  HeadlineWithSupertextComponent,
  withAnimationContext,
  DELAYS,
} from '../../ui';
import { ModuleType } from '..';
import { fadeInAnimation } from '../../ui/Animations';

const FAQLinks = styled.div`
  background: ${({ theme }) => theme.box.background};
  color: ${({ theme }) => theme.box.text};
  display: flex;
  align-items: flex-start;

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    justify-content: center;
    padding-top: 0;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;

  ul {
    list-style: none;
    li {
      padding-top: ${spacer16};
      padding-bottom: ${spacer16};
      &:not(:first-child):not(:last-child) {
        border-top: 1px solid ${({ theme }) => hexToRgba(theme.box.text, 0.25)};
      }
    }
  }
`;

const AllFAQLink = styled(ButtonTertiaryAsLink)`
  padding: 0;
  color: ${({ theme }) => theme.box.text};

  &:hover:not(:focus):not(:disabled) {
    color: ${({ theme }) => hexToRgba(theme.box.text, 0.25)};
  }

  svg {
    vertical-align: -3px;
  }
`;

const FAQList = withAnimationContext(styled.ul`
  ${fadeInAnimation(DELAYS.THIRD)};
`);

const FAQ = styled(Type.p).attrs({ variant: Type.body1 })`
  color: ${({ theme }) => theme.box.text};
  padding: 0px;

  &:hover:not(:focus):not(:disabled) {
    color: ${({ theme }) => hexToRgba(theme.box.text, 0.25)};
  }

  @media (min-width: ${screenMdMin}) {
    text-align: left;
    justify-content: flex-end;
    width: 100%;
  }
  @media (max-width: ${screenSmMax}) {
    width: 100%;
    align-items: center;
  }
`;

export type FAQType = {
  question: string;
  urlSlug: string;
  answer: string;
  id: string;
  contentType: string;
};

export type FAQLinksModuleType = ModuleType & {
  supertext?: string;
  headline?: string;
  faqs?: FAQType[];
};

const TRACK_CAT = 'module-faqLinks';

export const FAQLinksModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  FAQLinksModuleType
>(({ supertext = '', headline = '', faqs = [] }, ref) => {
  return (
    <FAQLinks ref={ref as React.RefObject<HTMLDivElement>}>
      <Left>
        <HeadlineWithSupertextComponent
          supertext={supertext}
          headline={headline}
        />
      </Left>
      <Right>
        <FAQList>
          {faqs.map(faq => (
            <li key={faq.id}>
              <Link
                track={[TRACK_CAT, `FAQ Link - ${faq.question}`]}
                href={`/faq/entries/${faq.urlSlug}`}
              >
                <FAQ>{faq.question}</FAQ>
              </Link>
            </li>
          ))}
          <li>
            <AllFAQLink href="/faq" track={[TRACK_CAT, 'All FAQ Link']}>
              See all FAQs
              <IconArrowRight iconSize={16} />
            </AllFAQLink>
          </li>
        </FAQList>
      </Right>
    </FAQLinks>
  );
});
